<template>
  <lb-dialog
    v-model="dialogVisible"
    heading="Reporting Unit Settings"
    width="700"
    :loading="loading"
    data-testid="reporting-unit-settings-dialog"
  >
    <template v-slot:body>
      <div data-testid="reporting-unit-settings-dialog__body">
        <!-- RU Info Header -->
        <div class=" mb-2 noTransform" data-testid="reporting-unit-settings-dialog__ru-title">{{ ruData.code }} - {{ ruData.name }}</div>
        <div class="mb-5" data-testid="reporting-unit-settings-dialog__ru-info">
          <v-chip small label class="mr-1" data-testid="reporting-unit-settings-dialog__network-chip"><span class="noTransform">{{ ruData.network }}</span></v-chip>
          <v-chip small label class="mr-1" data-testid="reporting-unit-settings-dialog__region-chip"><span class="noTransform">{{ ruData.region }}</span></v-chip>
          <v-chip small label class="mr-1" data-testid="reporting-unit-settings-dialog__country-chip"><span class="noTransform">{{ ruData.country }}</span></v-chip>
        </div>

        <!-- Tabs -->
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          color="primary"
          data-testid="reporting-unit-settings-dialog__tabs"
        >
          <v-tab data-testid="reporting-unit-settings-dialog__workflow-tab">Workflow</v-tab>
          <v-tab data-testid="reporting-unit-settings-dialog__other-tab">Other</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="mt-4" data-testid="reporting-unit-settings-dialog__tabs-content">
          <!-- Tab 1: Workflow -->
          <v-tab-item data-testid="reporting-unit-settings-dialog__workflow-panel">
            <div>
              <div class="" data-testid="reporting-unit-settings-dialog__proforma-heading">
                {{
                  $nova.labelsName(
                    "my_reporting_units_page",
                    "proforma_shortform_label",
                    "Proforma"
                  )
                }}
              </div>
              <div class="d-flex" data-testid="reporting-unit-settings-dialog__proforma-local-controls">
                <v-checkbox
                  dense
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_local_key_label',
                      'Local'
                    ) + ' Control'
                  "
                  v-model="workflowData.localcontrol_proforma"
                  data-testid="reporting-unit-settings-dialog__localcontrol-proforma-checkbox"
                ></v-checkbox>
                <v-checkbox
                  dense
                  class="ml-4"
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_local_key_label',
                      'Local'
                    ) + ' Management'
                  "
                  v-model="workflowData.localmanagement_proforma"
                  data-testid="reporting-unit-settings-dialog__localmanagement-proforma-checkbox"
                ></v-checkbox>
              </div>
              <div class="d-flex" data-testid="reporting-unit-settings-dialog__proforma-regional-controls">
                <v-checkbox
                  dense
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_regional_key_label',
                      'Regional'
                    ) + ' Control'
                  "
                  v-model="workflowData.regionalcontrol_proforma"
                  data-testid="reporting-unit-settings-dialog__regionalcontrol-proforma-checkbox"
                ></v-checkbox>
                <v-checkbox
                  dense
                  class="ml-4"
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_regional_key_label',
                      'Regional'
                    ) + ' Management'
                  "
                  v-model="workflowData.regionalmanagement_proforma"
                  data-testid="reporting-unit-settings-dialog__regionalmanagement-proforma-checkbox"
                ></v-checkbox>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="" data-testid="reporting-unit-settings-dialog__forecast-heading">
                {{
                  $nova.labelsName(
                    "my_reporting_units_page",
                    "forecast_shortform_label",
                    "Forecast"
                  )
                }}
              </div>
              <div class="d-flex" data-testid="reporting-unit-settings-dialog__forecast-local-controls">
                <v-checkbox
                  dense
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_local_key_label',
                      'Local'
                    ) + ' Control'
                  "
                  v-model="workflowData.localcontrol_forecast"
                  data-testid="reporting-unit-settings-dialog__localcontrol-forecast-checkbox"
                ></v-checkbox>
                <v-checkbox
                  dense
                  class="ml-4"
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_local_key_label',
                      'Local'
                    ) + ' Management'
                  "
                  v-model="workflowData.localmanagement_forecast"
                  data-testid="reporting-unit-settings-dialog__localmanagement-forecast-checkbox"
                ></v-checkbox>
              </div>
              <div class="d-flex" data-testid="reporting-unit-settings-dialog__forecast-regional-controls">
                <v-checkbox
                  dense
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_regional_key_label',
                      'Regional'
                    ) + ' Control'
                  "
                  v-model="workflowData.regionalcontrol_forecast"
                  data-testid="reporting-unit-settings-dialog__regionalcontrol-forecast-checkbox"
                ></v-checkbox>
                <v-checkbox
                  dense
                  class="ml-4"
                  hide-details
                  :label="
                    $nova.labelsName(
                      'my_reporting_units_page',
                      'commentary_regional_key_label',
                      'Regional'
                    ) + ' Management'
                  "
                  v-model="workflowData.regionalmanagement_forecast"
                  data-testid="reporting-unit-settings-dialog__regionalmanagement-forecast-checkbox"
                ></v-checkbox>
              </div>
              <div class="mt-4 info--text text-body-2" data-testid="reporting-unit-settings-dialog__workflow-note">
                <v-icon small color="info" data-testid="reporting-unit-settings-dialog__workflow-note-icon">mdi-information</v-icon>
                <b>Note:</b> If, as a result of this submission, a commentary cycle crosses control threshold either at {{ $nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'local') }} or {{ $nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'regional') }} level, then this functionality will not revert the {{ $nova.labelsName('my_reporting_units_page', 'commenrary_ru_label', 'RU') }} level approval already done for such commentary cycle.
              </div>
            </div>
          </v-tab-item>

          <!-- Tab 2: Other Settings -->
          <v-tab-item data-testid="reporting-unit-settings-dialog__other-panel">
            <div>
              <!-- Bulk Approve Control -->
              <div class="mb-4" data-testid="reporting-unit-settings-dialog__bulk-approve-section">
                <div class=" mb-2" data-testid="reporting-unit-settings-dialog__bulk-approve-heading">
                  Bulk approve
                  <v-tooltip right max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="ml-1"
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        data-testid="reporting-unit-settings-dialog__bulk-approve-info-icon"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span data-testid="reporting-unit-settings-dialog__bulk-approve-tooltip">
                      Enabling this feature allows the {{ $nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local') }} and {{ $nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional') }} Approvers to approve or reject general ledger line items which have crossed control thresholds on a bulk basis either using multi-select option within the application or through file upload options.
                    </span>
                  </v-tooltip>
                </div>
                <v-checkbox
                  dense
                  hide-details
                  label="Enable Bulk Approve Control"
                  v-model="otherSettingsData.bulkapprove_control"
                  :disabled="!isAnyControlWorkflowEnabled"
                  data-testid="reporting-unit-settings-dialog__bulkapprove-control-checkbox"
                >
                </v-checkbox>
                <div
                  class="error--text text-caption mt-1"
                  v-if="!isAnyControlWorkflowEnabled"
                  data-testid="reporting-unit-settings-dialog__bulk-approve-disabled-message"
                >
                </div>
              </div>

              <v-divider class="my-3"></v-divider>

              <!-- SOD (Future Implementation) -->
              <div class="mb-4" data-testid="reporting-unit-settings-dialog__sod-section">
                <div class=" mb-2" data-testid="reporting-unit-settings-dialog__sod-heading">Segregation of duties
                   <v-tooltip right max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="ml-1"
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        data-testid="reporting-unit-settings-dialog__sod-info-icon"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span data-testid="reporting-unit-settings-dialog__sod-tooltip">
                        Disabling SOD (Segregation of Duties) allows a user to be assigned more than one role ({{ $nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'local') }} approver, {{ $nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'regional') }} approver or {{ $nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'local') }} preparer) with respect to a unit.
                    </span>
                  </v-tooltip> 
                </div>
                <v-checkbox
                  dense
                  hide-details
                  label="Enable SOD"
                  v-model="otherSettingsData.sodEnabled"
                  data-testid="reporting-unit-settings-dialog__sod-enabled-checkbox"
                >
                </v-checkbox>
              </div>

              <v-divider class="my-3"></v-divider>

              <!-- Auto Submission -->
              <div class="mb-4" data-testid="reporting-unit-settings-dialog__auto-submission-section">
                <div class=" mb-2" data-testid="reporting-unit-settings-dialog__auto-submission-heading">
                  Regional level auto submission
                  <v-tooltip right max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        class="ml-1"
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        data-testid="reporting-unit-settings-dialog__auto-submission-info-icon"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span data-testid="reporting-unit-settings-dialog__auto-submission-tooltip">
                      When enabled, the {{$nova.labelsName('my_reporting_units_page', 'commentary_local_approver_label','Local Approver') }} can simultaneously approve {{ $nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'local') }} comments and submit comments for {{ $nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'regional') }} approval by clicking approve and submit button.
                    </span>
                  </v-tooltip>
                </div>
                <v-checkbox
                  dense
                  hide-details
                  label="Enable Auto Submission"
                  v-model="otherSettingsData.autoSubmissionEnabled"
                  data-testid="reporting-unit-settings-dialog__auto-submission-enabled-checkbox"
                >
            </v-checkbox>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>

    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn small color="primary" @click="submitSettings" data-testid="reporting-unit-settings-dialog__update-button"><span>Update</span></v-btn>
    </template>
  </lb-dialog>
</template>
<script>
import varianceAnalysisConstants from "../constants"
import { UPDATE_RU_SETTINGS } from "../utils/constants";
export default {
  name: "ReportingUnitSettingsDialog",
  props: {
    value: {
      type: Boolean,
      required: true,
      default: false,
    },
    reportingUnitData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    defaultTab: {
      type: String,
      default: varianceAnalysisConstants.TAB_NAMES.WORKFLOW,
      validator: (value) => [varianceAnalysisConstants.TAB_NAMES.WORKFLOW, varianceAnalysisConstants.TAB_NAMES.OTHER].includes(value),
    },
  },
  data() {
    return {
      loading: false,
      activeTab: varianceAnalysisConstants.TABS.WORKFLOW,
      workflowData: {
        localcontrol_proforma: false,
        localmanagement_proforma: false,
        regionalcontrol_proforma: false,
        regionalmanagement_proforma: false,
        localcontrol_forecast: false,
        localmanagement_forecast: false,
        regionalcontrol_forecast: false,
        regionalmanagement_forecast: false,
      },
      otherSettingsData: {
        bulkapprove_control: false,
        sodEnabled: false,
        autoSubmissionEnabled: false,
      },
      modifiedTabs: new Set(), // Track which tabs have changes
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    ruData() {
      return this.reportingUnitData || {};
    },
    isAnyControlWorkflowEnabled() {
      return (
        this.workflowData.localcontrol_proforma ||
        this.workflowData.localcontrol_forecast ||
        this.workflowData.regionalcontrol_proforma ||
        this.workflowData.regionalcontrol_forecast
      );
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeData();
      }
    },
    reportingUnitData: {
      deep: true,
      handler() {
        if (this.value) {
          this.initializeData();
        }
      },
    },
    defaultTab: {
      immediate: true,
      handler(newVal) {
        this.activeTab = varianceAnalysisConstants.TABS[newVal];
      },
    },
    isAnyControlWorkflowEnabled(newVal) {
      // Auto-disable bulk approve if no control workflows are enabled
      if (!newVal) {
        this.otherSettingsData.bulkapprove_control = false;
      }
    },
    workflowData: {
      deep: true,
      handler() {
        this.modifiedTabs.add(varianceAnalysisConstants.TABS.WORKFLOW);
      },
    },
    otherSettingsData: {
      deep: true,
      handler() {
        this.modifiedTabs.add(varianceAnalysisConstants.TABS.OTHER);
      },
    },
  },
  methods: {
    initializeData() {
      // Initialize workflow data
      this.workflowData = {
        localcontrol_proforma: this.ruData?.localcontrol_proforma || false,
        localmanagement_proforma: this.ruData?.localmanagement_proforma || false,
        regionalcontrol_proforma: this.ruData?.regionalcontrol_proforma || false,
        regionalmanagement_proforma: this.ruData?.regionalmanagement_proforma || false,
        localcontrol_forecast: this.ruData?.localcontrol_forecast || false,
        localmanagement_forecast: this.ruData?.localmanagement_forecast || false,
        regionalcontrol_forecast: this.ruData?.regionalcontrol_forecast || false,
        regionalmanagement_forecast: this.ruData?.regionalmanagement_forecast || false,
      };

      // Initialize other settings data
      this.otherSettingsData = {
        bulkapprove_control: this.ruData?.bulkapprove_control || false,
        sodEnabled: this.ruData?.sodEnabled != null ? this.ruData.sodEnabled : true,
        autoSubmissionEnabled: this.ruData?.autoSubmissionEnabled || false,
      };
      this.modifiedTabs.clear(); // Clear modified tabs when initializing data
      // Set active tab
      this.activeTab =  varianceAnalysisConstants.TABS[this.defaultTab];
    },
    closeDialog() {
      this.$emit("input", false);
      this.$emit("close");
    },
    async submitSettings() {
      this.loading = true;

      try {
        const promises = [];
        if (this.modifiedTabs.has(varianceAnalysisConstants.TABS.WORKFLOW)) {
          // Submit workflow settings
          promises.push(
            this.$store.dispatch(
              UPDATE_RU_SETTINGS.DISPATCHERS.UPDATE_REPORTING_UNIT_WORKFLOW_SETTINGS,
              {
                reportingUnitId: this.ruData._id,
                workflowData: this.workflowData,
                axiosInstance: this.axios,
              }
            )
          );
        } 
        if(this.modifiedTabs.has(varianceAnalysisConstants.TABS.OTHER)) {
          // Submit other settings
          promises.push(
            this.$store.dispatch(
                UPDATE_RU_SETTINGS.DISPATCHERS.UPDATE_REPORTING_UNIT_OTHER_SETTINGS,
                {
                  reportingUnitId: this.ruData._id,
                otherSettingsData: this.otherSettingsData,
                isAnyControlWorkflowEnabled: this.isAnyControlWorkflowEnabled,
                axiosInstance: this.axios,
              }
            )
          );
         }
        // If nothing was modified, just close
        if (promises.length === 0) {
          this.closeDialog();
          return;
        }
        // Execute all modified settings
        await Promise.all(promises);

        this.$store.commit("sbSuccess", varianceAnalysisConstants.MESSAGES.SETTINGS_UPDATED_SUCCESSFULLY);
        this.$emit(varianceAnalysisConstants.STATUS.SUCCESS, {
          tab: this.activeTab,
          workflowData: this.workflowData,
          otherSettingsData: this.otherSettingsData,
        });
        this.closeDialog();
      } catch (err) {
        this.$store.commit(
          "sbError",
          err.message || err || varianceAnalysisConstants.MESSAGES.FAILED_TO_UPDATE_SETTINGS
        );
        console.error(err);
        this.$emit("error", err);
      } finally {
        this.loading = false;
      }
    }
  },
  
};
</script>